import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { useCreateAsset, useUpdateAsset } from '@/hooks/usePartnerships';
import type { Asset, AssetType, AssetEntityType } from '../../../../shared/types/partnerships';
import { toLines, fromLines } from '../../../../shared/text-lines';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  asset?: Asset;
  prefillEntityId?: string;
  prefillEntityType?: AssetEntityType;
}

const ASSET_TYPE_VALUES: AssetType[] = ['one-pager', 'deck', 'video', 'image', 'creative-example', 'other'];
const ENTITY_TYPE_VALUES: AssetEntityType[] = ['product', 'package', 'program', 'case', 'framework'];

const STATUSES = ['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const;
// Radix Select (>=2.x) lanza un error si un <SelectItem> tiene value="" y eso tira
// abajo toda la página (no hay ErrorBoundary). La opción "ninguno" usa este
// centinela y se traduce a '' al guardar en el form.
const NONE = '__none__';

interface FormData {
  name: string; assetType: string; driveUrl: string;
  description: string; tags: string; fileFormat: string;
  thumbnailUrl: string; entityId: string; entityType: string; status: string;
}


export function AssetFormDialog({
  open, onOpenChange, partnerId, asset,
  prefillEntityId, prefillEntityType,
}: Props) {
  const { t } = useTranslation();
  const isEditing = !!asset;
  const create = useCreateAsset();
  const update = useUpdateAsset();

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>();
  const statusValue = watch('status');
  const assetTypeValue = watch('assetType');
  const entityTypeValue = watch('entityType');

  useEffect(() => {
    if (open) {
      reset({
        name: asset?.name || '', assetType: asset?.assetType || 'one-pager',
        driveUrl: asset?.driveUrl || '', description: asset?.description || '',
        tags: toLines(asset?.tags), fileFormat: asset?.fileFormat || '',
        thumbnailUrl: asset?.thumbnailUrl || '',
        entityId: asset?.entityId || prefillEntityId || '',
        entityType: asset?.entityType || prefillEntityType || '',
        status: asset?.status || 'PUBLISHED',
      });
    }
  }, [open, asset, prefillEntityId, prefillEntityType, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      name: data.name, assetType: data.assetType as AssetType,
      driveUrl: data.driveUrl, description: data.description || undefined,
      tags: fromLines(data.tags),
      fileFormat: data.fileFormat || undefined,
      thumbnailUrl: data.thumbnailUrl || undefined,
      entityId: data.entityId || undefined,
      entityType: data.entityType as AssetEntityType || undefined,
      status: data.status as any,
    };

    if (isEditing) {
      await update.mutateAsync({ assetId: asset!.assetId, ...payload, expectedUpdatedAt: asset!.updatedAt });
    } else {
      await create.mutateAsync({ partnerId, ...payload });
    }
    onOpenChange(false);
  };

  const isBusy = create.isPending || update.isPending;

  const assetTypeLabel = (value: AssetType): string => {
    const map: Record<AssetType, string> = {
      'one-pager': t('partnerships.asset_form.type_one_pager'),
      'deck': t('partnerships.asset_form.type_deck'),
      'video': t('partnerships.asset_form.type_video'),
      'image': t('partnerships.asset_form.type_image'),
      'creative-example': t('partnerships.asset_form.type_creative_example'),
      'other': t('partnerships.asset_form.type_other'),
    };
    return map[value] ?? value;
  };

  const entityTypeLabel = (value: AssetEntityType): string => {
    const map: Record<AssetEntityType, string> = {
      'product': t('partnerships.asset_form.entity_product'),
      'package': t('partnerships.asset_form.entity_package'),
      'program': t('partnerships.asset_form.entity_program'),
      'case': t('partnerships.asset_form.entity_case'),
      'framework': t('partnerships.asset_form.entity_framework'),
    };
    return map[value] ?? value;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('partnerships.asset_form.title_edit') : t('partnerships.asset_form.title_create')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.name_required')}</Label>
            <Input {...register('name', { required: true })} placeholder={t('partnerships.asset_form.one_pager_tiktok_q4_2025')} />
            {errors.name && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.asset_form.type_label')}</Label>
              <Select value={assetTypeValue} onValueChange={v => setValue('assetType', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSET_TYPE_VALUES.map(v => (
                    <SelectItem key={v} value={v}>{assetTypeLabel(v)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.asset_form.format_label')}</Label>
              <Input {...register('fileFormat')} placeholder={t('partnerships.asset_form.pdf_pptx_mp4')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.asset_form.drive_link_required_label')}</Label>
            <Input {...register('driveUrl', { required: true })} type="url" placeholder="https://drive.google.com/..." />
            {errors.driveUrl && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.asset_form.description_optional_label')}</Label>
            <Textarea {...register('description')} rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.asset_form.tags_label')}</Label>
            <Textarea {...register('tags')} rows={2} placeholder="tiktok&#10;onepager&#10;2025" />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.asset_form.thumbnail_url_label')}</Label>
            <Input {...register('thumbnailUrl')} type="url" placeholder="https://..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.asset_form.entity_type_label')}</Label>
              <Select value={entityTypeValue || NONE} onValueChange={v => setValue('entityType', v === NONE ? '' : v)}>
                <SelectTrigger><SelectValue placeholder={t('partnerships.asset_form.no_entity')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('partnerships.asset_form.no_entity')}</SelectItem>
                  {ENTITY_TYPE_VALUES.map(v => (
                    <SelectItem key={v} value={v}>{entityTypeLabel(v)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.asset_form.entity_id_label')}</Label>
              <Input {...register('entityId')} placeholder={t('partnerships.asset_form.entity_id_placeholder')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.status_label')}</Label>
            <Select value={statusValue} onValueChange={v => setValue('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel')}</Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? t('partnerships.common.saving') : isEditing ? t('partnerships.common.save_changes') : t('partnerships.asset_form.create_button')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
